/**
 * 报表中心预览 · 秦艳芳 · **素质分来自 DB 快照**
 * 数据源：`db-backups/talentgrid-20260428-004312-v1.3-score-calibration.sql`
 * - 被评价人 Employee.id = 54（秦艳芳），batchId = 2
 * - EvaluationTask：(1768 自评) (1769 直属·郭天山) (1770 斜线·梁钰珊)
 *                   (1771 下属视角·原冬兰) (1772 同级管理者·李美婷)
 * - 每项为 EvaluationScore 中 INITIATIVE / DEDICATION / DELIVERY / RESILIENCE / PUBLIC_FIRST，
 *   维度中文名见 `seed-quality-templates.ts`.
 *
 * **若后续库中分数变更**：用同批次重新导出或改写下方 `RAW_SCORES`。
 */

import type { PersonalReportSummaryJson } from './mockPersonalReportSummary';

const DIM_ROWS = [
  { code: 'INITIATIVE', name: '积极主动' },
  { code: 'DEDICATION', name: '敬业精神' },
  { code: 'DELIVERY', name: '卓越交付' },
  { code: 'RESILIENCE', name: '坚韧抗压' },
  { code: 'PUBLIC_FIRST', name: '先公后私' },
] as const;

type EvalRoleSlot = {
  roleLabel: string;
  evaluatorName: string;
  submitAt: string;
  scores: number[];
};

/**
 * talentgrid 20260428 backup · taskId 1768～1772 已对齐提交时间线（SUBMITTED）
 */
const RAW_SCORES: EvalRoleSlot[] = [
  {
    roleLabel: '自评',
    evaluatorName: '秦艳芳',
    submitAt: '2026-04-27T16:10:00.925Z',
    scores: [1, 1, 1, 1, 1],
  },
  {
    roleLabel: '直属领导',
    evaluatorName: '郭天山',
    submitAt: '2026-04-27T16:23:47.868Z',
    scores: [2, 1, 1, 7, 7],
  },
  {
    roleLabel: '斜线/带教',
    evaluatorName: '梁钰珊',
    submitAt: '2026-04-27T16:12:20.879Z',
    scores: [5, 5, 5, 5, 5],
  },
  {
    roleLabel: '下属',
    evaluatorName: '原冬兰',
    submitAt: '2026-04-27T16:11:36.675Z',
    scores: [6, 6, 6, 6, 6],
  },
  {
    roleLabel: '同级管理者',
    evaluatorName: '李美婷',
    submitAt: '2026-04-27T16:12:58.141Z',
    scores: [4, 4, 4, 4, 4],
  },
];

const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

const buildBreakdown = () =>
  RAW_SCORES.map((r) => {
    const rowTotal = round1(r.scores.reduce((a, b) => a + b, 0));
    const avgPerDim = round2(rowTotal / DIM_ROWS.length);
    return {
      roleLabel: r.roleLabel,
      evaluatorName: r.evaluatorName,
      submitAt: r.submitAt,
      rowTotal,
      avgPerDim,
      dimensions: DIM_ROWS.map((dr, i) => ({
        dimCode: dr.code,
        dimName: dr.name,
        score: r.scores[i],
        comment: null as string | null,
      })),
    };
  });

const breakdown = buildBreakdown();
const rowTotals = breakdown.map((b) => b.rowTotal);
const maxTotal = Math.max(...rowTotals);
const minTotal = Math.min(...rowTotals);

const breakdownWithSummary = breakdown.map((b) => {
  let trend = '与各评价人行合计相近';
  if (b.rowTotal === maxTotal) trend = '相对其他角色，本条行合计为最高的一档';
  else if (b.rowTotal === minTotal) trend = '相对其他角色，本条行合计为最低的一档';
  const summaryLine = `${b.roleLabel}「${b.evaluatorName}」打分覆盖 ${DIM_ROWS.length} 项素质维度，行合计 ${b.rowTotal.toFixed(
    1,
  )}，折算单维均值约 ${b.avgPerDim.toFixed(2)} 分（标尺 0～7）。${trend}。以下为其各维度分值与评语。`;
  return { ...b, summaryLine };
});

const radar = DIM_ROWS.map((dr, i) => {
  const all = RAW_SCORES.map((r) => r.scores[i]);
  return {
    code: dr.code,
    label: dr.name,
    avg: round2(all.reduce((a, b) => a + b, 0) / all.length),
  };
});

const SELF_IDX = 0;
const DIRECT_IDX = 1;
const dimensionComparison = DIM_ROWS.map((dr, i) => {
  const dimName = dr.name;
  const selfScore = RAW_SCORES[SELF_IDX].scores[i];
  const directScore = RAW_SCORES[DIRECT_IDX].scores[i];
  const nonSelf = RAW_SCORES.filter((_, idx) => idx !== SELF_IDX).map((r) => r.scores[i]);
  const othersAvg = round2(nonSelf.reduce((a, b) => a + b, 0) / nonSelf.length);
  const spread = round2(selfScore - othersAvg);
  let note = '自评与非自评接近';
  if (spread > 0.5) note = '自评高于非自评均值';
  else if (spread < -0.5) note = '自评低于非自评均值';
  if (spread >= 1) note = '自评明显高于非自评均值';
  return {
    dimCode: dr.code,
    dimName,
    selfScore,
    directScore,
    othersAvg,
    spread,
    note,
  };
});

const insightsQualitative = [
  `数据快照：TalentGrid backup 2026-04-28（batchId=2，秦艳芳 evaluatee id=54，任务 1768～1772）。五人素质分已按库内 EvaluationScore 原值写入预览。`,
  `自评分项均为最低档，与他评离散极大；直属在「坚韧抗压」「先公后私」给到满分区，与前三维形成强烈对照——雷达上会呈现明显分叉。`,
];

export const MOCK_PERSONAL_REPORT_SUMMARY_QINYANFANG: PersonalReportSummaryJson = {
  emp: {
    id: 54,
    name: '秦艳芳',
    hireDate: '2015-09-01',
    deptPath: '广州瑞小美医疗美容股份有限公司广西分公司 / 行政人事部',
    position: '人事经理&瑞小美学苑执行秘书长',
    nineBoxResults: [
      {
        qualityStandardScore: 4.08,
        qualityLevel: 'H',
        performanceLevel: 'H',
        finalCell: 'HH',
      },
    ],
    competencyAssessments: [
      {
        weightedScore: 4.28,
        evaluator: { name: '郭天山' },
        status: 'SUBMITTED',
        submitAt: '2026-03-26T14:22:00.000Z',
        redLines: null,
      },
    ],
    highPotentialAsEvaluatee: [{ resultLabel: '持续观察' }],
  },
  radar,
  tasks: RAW_SCORES.length,
  insightsQualitative,
  dimensionComparison,
  evaluationBreakdown: breakdownWithSummary,
  qualityAnalysisMeta: {
    advantageMeanThreshold: 5.5,
    concernMeanThreshold: 4,
    desc:
      '报告中「优势 / 常规 / 短板」：仅依据本期各素质维度上所有已提交 360° 评价的算术均分，与阈值比较——均分 ≥ 5.5 标为「优势」，< 4 标为「短板」，介于其间为「常规」。',
  },
  competencyRadar: [
    { axis: '组织人才供应链', score: 4.3 },
    { axis: '制度与文化落地', score: 4.2 },
    { axis: '沟通影响力', score: 4.4 },
    { axis: '变革推动', score: 4.1 },
    { axis: '合规与风控', score: 4.5 },
  ],
  competencyAssessment: {
    weightedScore: 4.28,
    evaluatorName: '郭天山',
    status: 'SUBMITTED',
    submitAt: '2026-03-26T14:22:00.000Z',
    redLines: null,
  },
  performanceScore: {
    score: 95,
    level: 'H',
    source: '月报汇总（示意）',
  },
  leaderComment:
    '以下为示例占位：正式上级建议仍以「直属评价」提交的文本为准。秦艳芳素质项数据来源见预览页首 insights 说明。',
  aiNarrative: {
    profile:
      '秦艳芳的评价数据呈现出显著的跨视角分歧，当前结论应以事实复盘而非平均分直接定档。自评明显保守，直属评价内部也高度分化；这组数据更适合作为校准议题入口，而非单独的人才结论。',
    suggestions:
      '1. 先组织一次评分口径校准，逐项追问极端分值背后的具体事件。\n2. 在 1on1 中用他评高分行为帮助本人校准长期偏低的自我评价。\n3. 补充关键项目与协作证据后，再进行二次定档讨论。',
  },
  aiEnabled: true,
  aiQueueStatus: 'READY',
};
